import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import type { PlanCutRow } from "../lib/planCuts";

/**
 * Plans for a tenant, for the Features veto caption (KLUB-173).
 *
 * SELECT on `tiers` currently uses legacy `is_platform_admin()`. Platform
 * sessions live in `klubora_platform_users` — the same hole KLUB-160 hit on
 * `feature_flags`. An empty list after a failed load must not look like
 * "nobody cut".
 */
export function useTenantPlanCuts(tenantId: string | null) {
  const [plans, setPlans] = useState<PlanCutRow[]>([]);
  const [loadedFor, setLoadedFor] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const loading = tenantId !== null && loadedFor !== tenantId;

  useEffect(() => {
    let active = true;
    if (!tenantId) {
      setPlans([]);
      setError(null);
      setLoadedFor(null);
      return;
    }

    void (async () => {
      const { data, error: queryError } = await supabase
        .from("tiers")
        .select("name, config")
        .eq("tenant_id", tenantId)
        .order("sort_order", { ascending: true });
      if (!active) return;
      if (queryError) {
        setError(queryError.message);
        setPlans([]);
        setLoadedFor(tenantId);
        return;
      }
      setError(null);
      setPlans((data as PlanCutRow[]) ?? []);
      setLoadedFor(tenantId);
    })();

    return () => { active = false; };
  }, [tenantId]);

  return { plans, loading, error };
}
